import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_STREAM } from 'api/streams/mutations';
import {
  UpdateStream,
  UpdateStreamVariables,
} from 'api/streams/types/UpdateStream';
import { GET_STREAM_MATCHUP_INVITES } from 'api/matchup/queries';
import { DECLARE_MATCHUP_WINNER } from 'api/matchup/mutations';
import {
  GetStreamMatchupInvites,
  GetStreamMatchupInvitesVariables,
} from 'api/matchup/types/GetStreamMatchupInvites';
import {
  DeclareMatchupWinner,
  DeclareMatchupWinnerVariables,
} from 'api/matchup/types/DeclareMatchupWinner';
import { MatchupCorner, UserRole } from 'api/graphql-global-types';
import { GetStream_stream } from 'api/streams/types/GetStream';
import { useDebouncedSave, useGetCurrUser } from 'hooks';
import { getIsInterview } from 'helpers/getIsInterview';
import { getProperErrorMessage } from 'helpers/errors';
import { showToast } from 'components/common/Toast/Toast';
import { useGetStreamMatchups } from './useGetStreamMatchups';
import { draftToMatchupInput, matchupToDraft } from './fightCardHelpers';
import { FightCompetitorDraft, FightDraft } from './fightCardTypes';

export const useFightCardStudio = (stream: GetStream_stream) => {
  const { data: currentUserData } = useGetCurrUser();
  const isInterview = getIsInterview(stream);

  const isPaidStream = stream.price > 0 && !isInterview;

  const {
    matchups,
    refetch: refetchMatchups,
    loading: matchupsLoading,
  } = useGetStreamMatchups(stream.id, { skip: !isPaidStream });

  const isHost = currentUserData?.me?.id === stream.store?.id;
  const isModerator = stream.moderator?.id === currentUserData?.me?.id;
  const isAdmin = currentUserData?.me?.role === UserRole.Admin;
  const canControlFights = isHost || isModerator || isAdmin;

  const [fightDrafts, setFightDrafts] = useState<FightDraft[]>([]);
  const [matchupsModalOpen, setMatchupsModalOpen] = useState<boolean>(false);
  const isEvent = (matchups?.length ?? 0) > 0 || fightDrafts.length > 0;

  const fightsSeededRef = useRef(false);
  useEffect(() => {
    if (fightsSeededRef.current || matchupsLoading) {
      return;
    }
    fightsSeededRef.current = true;
    if (matchups.length) {
      setFightDrafts(matchups.map(matchupToDraft));
    }
  }, [matchupsLoading, matchups]);

  const { data: invitesData, refetch: refetchInvites } = useQuery<
    GetStreamMatchupInvites,
    GetStreamMatchupInvitesVariables
  >(GET_STREAM_MATCHUP_INVITES, {
    variables: { streamId: stream.id },
    skip: !isPaidStream || !canControlFights,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const invites = invitesData?.getStreamMatchupInvites;
    if (!invites?.length || !matchups.length) {
      return;
    }
    const inviteByKey = new Map(
      invites.map((invite) => [`${invite.matchupId}:${invite.corner}`, invite])
    );
    setFightDrafts((prev) =>
      prev.map((draft, index) => {
        const matchup = matchups[index];
        if (!matchup) {
          return draft;
        }
        const mergeSlot = (
          slot: FightCompetitorDraft,
          corner: MatchupCorner
        ): FightCompetitorDraft => {
          const invite = inviteByKey.get(`${matchup.id}:${corner}`);
          if (
            !invite ||
            slot.storeId ||
            slot.inviteToken ||
            slot.name !== (invite.name ?? '')
          ) {
            return slot;
          }
          return {
            ...slot,
            invitedEmail: invite.invitedEmail ?? null,
            inviteToken: invite.inviteToken,
          };
        };
        const fighterA = mergeSlot(draft.fighterA, MatchupCorner.A);
        const fighterB = mergeSlot(draft.fighterB, MatchupCorner.B);
        return fighterA === draft.fighterA && fighterB === draft.fighterB
          ? draft
          : { ...draft, fighterA, fighterB };
      })
    );
  }, [invitesData, matchups]);

  const [updateStream] = useMutation<UpdateStream, UpdateStreamVariables>(
    UPDATE_STREAM
  );

  const { save: persistFights } = useDebouncedSave((drafts: FightDraft[]) => {
    updateStream({
      variables: {
        input: {
          id: stream.id,
          name: stream.name ?? '',
          description: stream.description ?? '',
          scheduleDate: stream.scheduleDate,
          timeZone: {
            tzCode: stream.timeZone?.tzCode,
            offset: stream.timeZone?.offset,
          },
          matchups: drafts.map(draftToMatchupInput),
        },
      },
    })
      .then(() => {
        refetchMatchups();
        refetchInvites();
      })
      .catch(() => undefined);
  }, 800);

  const handleDraftsChange = useCallback(
    (next: FightDraft[]) => {
      setFightDrafts(next);
      if (canControlFights) {
        persistFights(next);
      }
    },
    [persistFights, canControlFights]
  );

  const [declareMatchupWinner] = useMutation<
    DeclareMatchupWinner,
    DeclareMatchupWinnerVariables
  >(DECLARE_MATCHUP_WINNER);

  const declareWinner = useCallback(
    (matchupId: string, winnerCorner: MatchupCorner | null) =>
      declareMatchupWinner({
        variables: { input: { matchupId, winnerCorner } },
      })
        .then(() => refetchMatchups())
        .catch((error) => {
          showToast({
            message: getProperErrorMessage(
              error,
              'Could not save the winner, please try again'
            ),
            type: 'error',
          });
        }),
    [declareMatchupWinner, refetchMatchups]
  );

  return {
    matchups,
    fightDrafts,
    handleDraftsChange,
    matchupsModalOpen,
    setMatchupsModalOpen,
    isPaidStream,
    isEvent,
    canControlFights,
    declareWinner,
  };
};
